<template>
    <div class="ds-ex-tab">
        <div class="ds-ex-toolbar">
            <ExchangePeriodFilter v-model="f.period" />
            <EmployeeFilter v-model="f.positionIds" label="Должность" search-placeholder="Найти должность" :options="positionOptions" />
            <ExchangeOptionFilter v-model="recordTypeModel" label="Тип" :options="RECORD_TYPE_OPTIONS" />
            <ExchangeOptionFilter v-if="showExchangeType" v-model="exchangeTypeModel" label="Тип биржи" :options="EXCHANGE_TYPE_OPTIONS" />
            <!-- Итог (result, target-contract §4): без выбора удалённые скрыты сервером. -->
            <ExchangeOptionFilter v-model="resultModel" label="Итог" multiple :options="RESULT_OPTIONS" />
            <div class="ds-ex-spacer"></div>
            <ExchangeSearch v-model="f.search" />
        </div>

        <ExchangeFilterChips v-if="chips.length" :chips="chips" :found="summary?.matched_count ?? null" :total="summary?.total_in_period ?? null" @remove="removeChip" @reset="resetFilters" />

        <div v-if="backgroundError" class="ds-ex-bg-error" role="status">
            <span>Не удалось обновить данные</span>
            <button type="button" class="ds-ex-link-btn" @click="load('background')">Повторить</button>
        </div>

        <div class="ds-ex-table" role="table" aria-label="Архив" :aria-busy="status === 'loading'">
            <div class="ds-ex-grid ds-ex-head ds-ex-grid-archive" role="row">
                <span>Время</span><span>Должность</span><span>Биржа</span><span>Итог</span><span>Сотрудник</span><span></span>
            </div>
            <template v-if="status === 'loading'">
                <div v-for="index in 6" :key="index" class="ds-ex-grid ds-ex-row ds-ex-grid-archive" aria-hidden="true">
                    <span class="ds-ex-skeleton w-[90px]"></span>
                    <span class="ds-ex-skeleton w-[160px]"></span>
                    <span class="ds-ex-skeleton w-[70px]"></span>
                    <span class="ds-ex-skeleton w-[70px]"></span>
                    <span class="ds-ex-skeleton w-[150px]"></span>
                    <span></span>
                </div>
            </template>
            <div v-else-if="status === 'error'" class="ds-ex-state" role="alert">
                <span>Не удалось загрузить архив</span>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="load('reset')">Повторить</button>
            </div>
            <div v-else-if="!items.length" class="ds-ex-state">
                <template v-if="hasConditions">
                    <span>Ничего не нашли</span>
                    <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="resetFilters">Сбросить всё</button>
                </template>
                <span v-else>В архиве за период ничего нет</span>
            </div>
            <template v-else>
                <template v-for="entry in entries" :key="entry.key">
                    <div v-if="entry.type === 'day'" class="ds-ex-day">
                        <span class="ds-ex-strong">{{ entry.label }}</span>
                    </div>
                    <button
                        v-else
                        type="button"
                        class="ds-ex-grid ds-ex-row ds-ex-grid-archive ds-ex-row-button"
                        :aria-label="`История: ${entry.vacancy.position.name}, ${timeRange(entry.vacancy)}`"
                        @click="emit('open', entry.vacancy, 'history')"
                    >
                        <span class="ds-ex-muted">{{ timeRange(entry.vacancy) }}</span>
                        <span class="ds-ex-position">
                            <span class="ds-ex-ellipsis">{{ entry.vacancy.position.name }}</span>
                            <span v-if="entry.vacancy.isRequest" class="ds-ex-chip is-request is-small">Запрос</span>
                        </span>
                        <span class="ds-ex-muted">{{ EXCHANGE_TYPE_LABELS[entry.vacancy.exchangeType] }}</span>
                        <span><span v-if="archiveChip(entry.vacancy)" class="ds-ex-chip" :class="`is-${archiveChip(entry.vacancy)!.tone}`">{{ archiveChip(entry.vacancy)!.label }}</span></span>
                        <span class="ds-ex-responses">
                            <template v-if="entry.vacancy.assigned">
                                <ExchangeAvatar :person="entry.vacancy.assigned" />
                                <span class="ds-ex-ellipsis">{{ fullName(entry.vacancy.assigned) }}</span>
                            </template>
                            <span v-else-if="entry.vacancy.deletionReason" class="ds-ex-muted ds-ex-ellipsis" :title="entry.vacancy.deletionReason">{{ entry.vacancy.deletionReason }}</span>
                            <span v-else class="ds-ex-muted">—</span>
                        </span>
                        <span class="ds-ex-right ds-ex-muted" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>
                        </span>
                    </button>
                </template>
            </template>
        </div>

        <Pager v-if="status !== 'error'" :total="count" v-model:page="f.page" v-model:page-size="f.pageSize" />
    </div>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import EmployeeFilter from '@/components/employees/EmployeeFilter.vue'
import Pager from '@/components/employees/Pager.vue'
import ExchangeAvatar from './ExchangeAvatar.vue'
import ExchangeFilterChips, { type ExchangeChip } from './ExchangeFilterChips.vue'
import ExchangeOptionFilter from './ExchangeOptionFilter.vue'
import ExchangePeriodFilter from './ExchangePeriodFilter.vue'
import ExchangeSearch from './ExchangeSearch.vue'
import { useExchangeDirectory } from './exchangeDirectory'
import { useExchangePage } from './exchangePage'
import { useExchangeList } from './useExchangeList'
import {
    ARCHIVE_RESULT_LABELS,
    archiveChip,
    dayHeaderLabel,
    dayKeyOf,
    EXCHANGE_TYPE_LABELS,
    fullName,
    timeRange,
} from './exchangeFormat'
import type { ArchiveResult, ExchangeType, ListQuery, RecordType, Vacancy } from './exchangeTypes'

const props = defineProps<{ active: boolean }>()
// Архив только для чтения: строка открывает историю.
const emit = defineEmits<{ open: [vacancy: Vacancy, tab: 'vacancy' | 'history'] }>()

const RECORD_TYPE_OPTIONS = [{ id: 'vacancy', name: 'Вакансии' }, { id: 'request', name: 'Запросы' }]
const EXCHANGE_TYPE_OPTIONS = (Object.keys(EXCHANGE_TYPE_LABELS) as ExchangeType[]).map(id => ({ id, name: EXCHANGE_TYPE_LABELS[id] }))
// Удалённые скрыты по умолчанию и доступны только через «Итог → Удалена».
const RESULT_OPTIONS = (Object.keys(ARCHIVE_RESULT_LABELS) as ArchiveResult[]).map(id => ({ id, name: ARCHIVE_RESULT_LABELS[id] }))

const authStore = useAuthStore()
const page = useExchangePage()
const f = page.archive
const directory = useExchangeDirectory()

const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))
const positionOptions = computed(() => directory.positions.value)

const query = computed<ListQuery>(() => ({
    page: f.page,
    pageSize: f.pageSize,
    period: f.period,
    search: f.search,
    positionIds: f.positionIds,
    isUrgent: null,
    exchangeType: f.exchangeType,
    recordType: f.recordType,
    result: f.result,
}))

// Объявлено до списка: сброс страницы применяется в том же цикле, без второго запроса.
watch(() => [f.period.join(), f.positionIds.join(), f.recordType, f.exchangeType, f.result.join(), f.search, f.pageSize], () => { f.page = 1 })

const { items, count, status, backgroundError, summary, load } = useExchangeList('archive', query, {
    active: toRef(props, 'active'),
    onPageOverflow: last => { f.page = last },
})

const recordTypeModel = computed<string[]>({
    get: () => f.recordType ? [f.recordType] : [],
    set: value => { f.recordType = (value[0] as RecordType | undefined) ?? null },
})
const exchangeTypeModel = computed<string[]>({
    get: () => f.exchangeType ? [f.exchangeType] : [],
    set: value => { f.exchangeType = (value[0] as ExchangeType | undefined) ?? null },
})
const resultModel = computed<string[]>({
    get: () => f.result,
    set: value => { f.result = value as ArchiveResult[] },
})

const hasConditions = computed(() => Boolean(f.positionIds.length || f.recordType || f.exchangeType || f.result.length || f.search))

const chips = computed<ExchangeChip[]>(() => {
    const list: ExchangeChip[] = f.positionIds.map(id => ({ key: `position:${id}`, label: directory.positions.value.find(item => item.id === id)?.name ?? 'Должность' }))
    if (f.recordType) list.push({ key: 'type', label: f.recordType === 'request' ? 'Запросы' : 'Вакансии' })
    if (f.exchangeType) list.push({ key: 'exchange', label: EXCHANGE_TYPE_LABELS[f.exchangeType] })
    for (const result of f.result) list.push({ key: `result:${result}`, label: ARCHIVE_RESULT_LABELS[result] })
    return list
})

function removeChip(key: string) {
    if (key.startsWith('position:')) f.positionIds = f.positionIds.filter(id => `position:${id}` !== key)
    else if (key === 'type') f.recordType = null
    else if (key === 'exchange') f.exchangeType = null
    else if (key.startsWith('result:')) f.result = f.result.filter(id => `result:${id}` !== key)
}

function resetFilters() {
    f.positionIds = []
    f.recordType = null
    f.exchangeType = null
    f.result = []
    f.search = ''
}

type Entry = { type: 'day', key: string, label: string } | { type: 'row', key: string, vacancy: Vacancy }

// Порядок сервера (начало desc); заголовок дня — при смене дня, строки не переставляются.
const entries = computed<Entry[]>(() => {
    const result: Entry[] = []
    let last = ''
    for (const vacancy of items.value) {
        const day = dayKeyOf(vacancy.start)
        if (day !== last) {
            result.push({ type: 'day', key: `day:${day}`, label: dayHeaderLabel(day) })
            last = day
        }
        result.push({ type: 'row', key: vacancy.id, vacancy })
    }
    return result
})

defineExpose({ refresh: () => load('action') })
</script>
